import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { FiMail, FiPhone, FiMapPin, FiSend, FiCheckCircle, FiClock, FiMessageSquare, FiHeadphones } from 'react-icons/fi';
import api from '../utils/api';
import './Contact.css';

const Contact = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [recaptchaReady, setRecaptchaReady] = useState(false);

  useEffect(() => {
    // Check if reCAPTCHA is ready
    if (executeRecaptcha) {
      setRecaptchaReady(true);
      console.log('✅ reCAPTCHA is ready');
    } else {
      console.warn('⚠️  reCAPTCHA not ready yet');
    }
  }, [executeRecaptcha]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsSubmitting(true);

    try {
      // Execute reCAPTCHA if available
      let recaptchaToken = null;
      if (executeRecaptcha) {
        try {
          console.log('🔄 Executing reCAPTCHA...');
          recaptchaToken = await executeRecaptcha('contact_form');
          console.log('✅ reCAPTCHA token obtained:', recaptchaToken ? 'Yes (length: ' + recaptchaToken.length + ')' : 'No');
        } catch (recaptchaError) {
          console.error('❌ reCAPTCHA execution failed:', recaptchaError);
          alert('reCAPTCHA verification failed. Please refresh the page and try again.');
          setIsSubmitting(false);
          return;
        }
      } else {
        console.error('❌ reCAPTCHA not available');
        alert('reCAPTCHA is not loaded. Please refresh the page and try again.');
        setIsSubmitting(false);
        return;
      }
      
      if (!recaptchaToken) {
        console.error('❌ reCAPTCHA token is null');
        alert('reCAPTCHA verification failed. Please refresh the page and try again.');
        setIsSubmitting(false);
        return;
      }
      
      // Submit form with reCAPTCHA token
      console.log('📤 Submitting form with reCAPTCHA token...');
      await api.post('/contact/contact', {
        ...formData,
        recaptchaToken,
      });
      
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });

      setTimeout(() => {
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setIsSubmitting(false);
      
      // Get detailed error message
      let errorMessage = 'Failed to send message. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 403) {
        errorMessage = 'reCAPTCHA verification failed. Please refresh the page and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    }
  };

  const [contactInfo, setContactInfo] = useState([
    {
      icon: FiMail,
      label: 'Email',
      value: 'info@couriex.com',
      link: 'mailto:info@couriex.com',
    },
    {
      icon: FiPhone,
      label: 'Phone',
      value: '+971123456789',
      link: 'tel:+971123456789',
    },
    {
      icon: FiMapPin,
      label: 'Address',
      value: '123 Business St, City, Country',
      link: '#',
    },
  ]);

  const [businessHours, setBusinessHours] = useState([
    { day: 'Monday - Saturday', hours: '8:00 AM - 6:00 PM' },
    { day: 'Sunday', hours: 'Closed' },
  ]);

  useEffect(() => {
    fetchSiteSettings();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const response = await api.get('/site-settings');
      const settings = response.data?.data || response.data || {};
      
      setContactInfo([
        {
          icon: FiMail,
          label: 'Email',
          value: settings.company_email || 'info@couriex.com',
          link: `mailto:${settings.company_email || 'info@couriex.com'}`,
        },
        {
          icon: FiPhone,
          label: 'Phone',
          value: settings.company_phone || '+971123456789',
          link: `tel:${(settings.company_phone || '+971123456789').replace(/[^0-9+]/g, '')}`,
        },
        {
          icon: FiMapPin,
          label: 'Address',
          value: settings.company_address || '123 Business St, City, Country',
          link: '#',
        },
      ]);

      // Update business hours from settings and group them
      const mondayHours = settings.business_hours_monday || '8:00 AM - 6:00 PM';
      const tuesdayHours = settings.business_hours_tuesday || '8:00 AM - 6:00 PM';
      const wednesdayHours = settings.business_hours_wednesday || '8:00 AM - 6:00 PM';
      const thursdayHours = settings.business_hours_thursday || '8:00 AM - 6:00 PM';
      const fridayHours = settings.business_hours_friday || '8:00 AM - 6:00 PM';
      const saturdayHours = settings.business_hours_saturday || '8:00 AM - 6:00 PM';
      const sundayHours = settings.business_hours_sunday || 'Closed';

      // Group Monday-Saturday if they have the same hours
      const weekdaysHours = [mondayHours, tuesdayHours, wednesdayHours, thursdayHours, fridayHours, saturdayHours];
      const allWeekdaysSame = weekdaysHours.every(hours => hours === weekdaysHours[0]);
      
      const groupedHours = [];
      
      if (allWeekdaysSame) {
        // Group Monday-Saturday together
        groupedHours.push({
          day: 'Monday - Saturday',
          hours: weekdaysHours[0]
        });
      } else {
        // If different, show individually
        groupedHours.push(
          { day: 'Monday', hours: mondayHours },
          { day: 'Tuesday', hours: tuesdayHours },
          { day: 'Wednesday', hours: wednesdayHours },
          { day: 'Thursday', hours: thursdayHours },
          { day: 'Friday', hours: fridayHours },
          { day: 'Saturday', hours: saturdayHours }
        );
      }
      
      // Add Sunday separately
      groupedHours.push({
        day: 'Sunday',
        hours: sundayHours
      });
      
      setBusinessHours(groupedHours);
    } catch (error) {
      console.error('Error fetching site settings:', error);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <div className="contact page-transition">
      <Helmet>
        <title>Contact Us - Couriex</title>
        <meta name="description" content="Get in touch with Couriex. Contact us for courier and logistics services in UAE." />
      </Helmet>
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="contact-hero-overlay"></div>
        <div className="container">
          <motion.div
            className="contact-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="contact-hero-icon"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2, type: 'spring' }}
            >
              <FiMessageSquare />
            </motion.div>
            <h1 className="contact-hero-title">Get In Touch</h1>
            <p className="contact-hero-subtitle">
              We're here to help you. Reach out to us through any of the channels below, 
              or fill out the form and we'll get back to you as soon as possible.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Cards Section */}
      <section className="contact-cards-section">
        <div className="container">
          <motion.div
            className="contact-cards-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {contactInfo.map((info, index) => (
              <motion.a
                key={index}
                href={info.link}
                className="contact-card"
                variants={itemVariants}
                whileHover={{ y: -8, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <div className="contact-card-icon-wrapper">
                  <info.icon className="contact-card-icon" />
                </div>
                <h3 className="contact-card-title">{info.label}</h3>
                <p className="contact-card-value">{info.value}</p>
              </motion.a>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="contact-main-section">
        <div className="container">
          <div className="contact-main-grid">
            {/* Contact Form */}
            <motion.div
              className="contact-form-card"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="contact-form-header">
                <div className="contact-form-header-icon">
                  <FiSend />
                </div>
                <div>
                  <h2 className="contact-form-title">Send us a Message</h2>
                  <p className="contact-form-subtitle">
                    Fill out the form below and we'll respond within 24 hours.
                  </p>
                </div>
              </div>

              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">
                      <span>Full Name</span>
                      <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">
                      <span>Email Address</span>
                      <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+971123456789"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="subject">
                      <span>Subject</span>
                      <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder="What is this regarding?"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">
                    <span>Message</span>
                    <span className="required">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder="Tell us more about your inquiry..."
                  />
                </div>

                <motion.button
                  type="submit"
                  className="btn-submit"
                  disabled={isSubmitting || isSubmitted}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isSubmitting ? (
                    <>
                      <span className="btn-spinner"></span>
                      Sending...
                    </>
                  ) : isSubmitted ? (
                    <>
                      <FiCheckCircle />
                      Message Sent Successfully!
                    </>
                  ) : (
                    <>
                      <FiSend />
                      Send Message
                    </>
                  )}
                </motion.button>
              </form>
            </motion.div>

            {/* Contact Info Sidebar */}
            <motion.div
              className="contact-info-sidebar"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {/* Business Hours */}
              <div className="contact-info-card">
                <div className="contact-info-card-header">
                  <div className="contact-info-card-icon">
                    <FiClock />
                  </div>
                  <h3 className="contact-info-card-title">Business Hours</h3>
                </div>
                <div className="contact-hours-list">
                  {businessHours.map((schedule, index) => (
                    <div key={index} className="contact-hours-item">
                      <span className="contact-hours-day">{schedule.day}</span>
                      <span className="contact-hours-time">{schedule.hours}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Support Info */}
              <div className="contact-info-card">
                <div className="contact-info-card-header">
                  <div className="contact-info-card-icon">
                    <FiHeadphones />
                  </div>
                  <h3 className="contact-info-card-title">Customer Support</h3>
                </div>
                <p className="contact-info-card-description">
                  Our dedicated support team is available during business hours to assist you with any questions or concerns.
                </p>
                <div className="contact-support-info">
                  <a href={`mailto:${contactInfo[0]?.value}`} className="contact-support-link">
                    <FiMail />
                    Email Support
                  </a>
                  <a href={`tel:${contactInfo[1]?.value?.replace(/[^0-9+]/g, '')}`} className="contact-support-link">
                    <FiPhone />
                    Call Us Now
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Google Maps Section */}
      <section className="contact-map-section">
        <div className="container">
          <motion.div
            className="contact-map-wrapper"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="contact-map-header">
              <div className="contact-map-icon">
                <FiMapPin />
              </div>
              <div>
                <h2 className="contact-map-title">Find Us on Map</h2>
                <p className="contact-map-description">
                  Visit our location at the address shown on the map below.
                </p>
              </div>
            </div>
            <div className="contact-map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4036.9521889426246!2d55.3608241!3d25.227188099999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5d022669b32d%3A0x8c6a7a850f8bdd9b!2sCouriex%20Cargo%20%26%20Logistics%20LLC!5e1!3m2!1sen!2sae!4v1762338896032!5m2!1sen!2sae"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Couriex Cargo & Logistics LLC Location"
              ></iframe>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
